import React, { useEffect, useMemo, useRef, useState } from "react"
import { graphql } from "gatsby"

import CmsPage from "@utils/cmsPage"
import { trackCtaClick } from "@utils/trackEvent"
import {
  pickCollectionNode,
  resolveFaqCollection,
  resolveProofCollection,
} from "@utils/cmsCollections"
import industrySubpageMobileStyles from "@utils/industrySubpageMobileStyles"
import { getPrototypePageSlice } from "@utils/prototypeSlices"
import { agenciesSeoSections } from "../data/seoStorySections"

const prototypeSlice = getPrototypePageSlice("agencies")
const agenciesExtraStyles = `${prototypeSlice.extraStyles}${industrySubpageMobileStyles}

@media (max-width: 960px) {
  .cms-page .wl-pair {
    display: grid !important;
    grid-template-columns: minmax(0, 1fr) !important;
    gap: 16px !important;
    width: 100%;
    max-width: 420px;
    margin-left: auto;
    margin-right: auto;
  }

  .cms-page .wl-card {
    width: 100%;
    min-width: 0;
  }

  .cms-page .agency-stack-options {
    justify-content: center !important;
    max-width: 560px;
    margin-left: auto;
    margin-right: auto;
  }

  .cms-page .agency-stack-option {
    justify-content: center;
    flex: 0 1 132px;
    max-width: 180px;
  }

  .cms-page .pmock-body > div:first-child {
    align-items: center !important;
    flex-direction: column;
    gap: 8px;
    text-align: center;
  }

  .cms-page .ret-total {
    display: block;
    text-align: center;
  }

  .cms-page .ret-total .v {
    display: inline;
  }

  .cms-page .ret-total .delta {
    display: block;
    margin-left: 0;
    margin-top: 4px;
  }
}
`

const agencyHeroRetainers = [
  ["Northstar Outdoor", "SEO retainer", "$12K", "82%", "", "12K"],
  ["Rivermark Growth", "Paid media mgmt", "$18K", "64%", "var(--teal-dk)", "18K"],
  ["Brightline Search", "Content subscription", "$6K", "94%", "#f59e0b", "6K"],
  ["Harbor Local SEO", "Local SEO", "$3.2K", "40%", "", "3.2K"],
]

const wait = ms => new Promise(resolve => setTimeout(resolve, ms))

const agenciesFallbackTestimonials = [
  {
    quote:
      "We bill 40 SEO retainers a month. Card on file, auto-charges the 1st, dunning handled. My ops lead got her Sundays back and I stopped chasing AR.",
    customerName: "Jordan Reyes",
    customerInitials: "JR",
    customerRole: "Founder, Rivermark Growth",
    industry: "Agencies",
    brandName: "Rivermark Growth",
    metaOne: "Practice: Paid media + SEO",
    metaTwo: "Size: 24 people · Toronto",
    metaThree: "Service mix: 40% paid media · 35% SEO · 25% content",
    statOneValue: "$580K",
    statOneLabel: "Monthly retainer book",
    statTwoValue: "80%",
    statTwoLabel: "Less billing ops time",
    statThreeValue: "12 days",
    statThreeLabel: "Faster cash collection",
  },
  {
    quote:
      "Paid-media fees scale with spend. MoonClerk lets us tier the retainer to ad budget - when a client doubles their Meta spend, the management fee bumps automatically.",
    customerName: "Mika Chen",
    customerInitials: "MC",
    customerRole: "Ops, Brightline Search",
    industry: "Agencies",
  },
  {
    quote:
      "Clients pay through pay.northstar-seo.com, with our logo. They think we built the billing system. Net-30 invoices are gone from our agency.",
    customerName: "Devon Park",
    customerInitials: "DP",
    customerRole: "Partner, Northstar Outdoor",
    industry: "Agencies",
  },
]

const agenciesFallbackFaqItems = [
  {
    question: "How is this different from a native Stripe checkout or Stripe Billing?",
    answer:
      "MoonClerk sits on top of Stripe. You keep the Stripe account, fees, and money movement; MoonClerk adds branded payment forms, no-code form building, retainer management, multi-client dashboards, and human support.",
  },
  {
    question: "Can SEO and paid-media retainers tier by ad spend or service level?",
    answer:
      "Yes. Retainers can be flat, tiered by service level, or tied to a usage variable like ad spend or campaign scope.",
  },
  {
    question: "Can we charge our clients on our own agency domain?",
    answer:
      "Yes. Every form can live on a custom subdomain such as pay.youragency.com or billing.clientname.com. We handle the TLS certificate; you set DNS once.",
  },
  {
    question: "Do you support multi-seat teams and role-based access for account managers?",
    answer:
      "Yes, on the Growth plan and up. Roles include Owner, Finance, Account Manager, and Read-only, with audit logging and Google Workspace SSO included.",
  },
  {
    question: "Can we mark up MoonClerk and resell it to our clients?",
    answer:
      "Not officially. MoonClerk is per-agency, and your clients do not need an account, but many agencies bundle the setup fee into onboarding scope.",
  },
]

const agenciesSectionOrder = {
  agencies_test_band: 10,
  agencies_whitelabel: 20,
  agencies_retainer_ops: 30,
  agencies_seo_how: 35,
  agencies_stack: 40,
  agencies_capabilities: 50,
  agencies_seo_why: 55,
  agencies_story_card: 60,
  agencies_faq: 70,
  agencies_final_cta: 80,
}

const ensureSection = (sections, section) =>
  sections.some(item => item?.sectionKey === section.sectionKey) ? sections : [...sections, section]

const cleanAgencyCopy = value =>
  String(value || "")
    .replace(/White-label/gi, "Branded")
    .replace(/white-label/gi, "branded")
    .replace(/No MoonClerk footer, no Stripe checkout redirect\.?/gi, "Branded payment links and custom domains for client billing.")
    .replace(/Overage alerts when a client burns through their content quota or hour bucket\.?/gi, "Retainer threshold reminders when a client approaches the ad-spend tier or service level you track.")
    .replace(/Overage alerts when a retainer is tracking hot(?: \(content quota, hours, or ad-spend tier\))?/gi, "Retainer threshold reminders for ad-spend tiers and service levels")
    .replace(/Try the builder/gi, "Explore for free")

const cleanAgencySectionItems = items =>
  Array.isArray(items)
    ? items.map(item => ({
        ...item,
        title: cleanAgencyCopy(item?.title),
        body: cleanAgencyCopy(item?.body),
      }))
    : items

const cleanAgencySection = section => ({
  ...section,
  eyebrow: cleanAgencyCopy(section?.eyebrow),
  headingHtml: cleanAgencyCopy(section?.headingHtml),
  summary: cleanAgencyCopy(section?.summary),
  primaryCtaLabel: cleanAgencyCopy(section?.primaryCtaLabel),
  secondaryCtaLabel: cleanAgencyCopy(section?.secondaryCtaLabel),
  contentItemEntries: cleanAgencySectionItems(section?.contentItemEntries),
})

const AgenciesHero = () => {
  const mockRef = useRef(null)
  const activeRef = useRef(true)
  const visibleRef = useRef(true)
  const [barsFilled, setBarsFilled] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const [totalBump, setTotalBump] = useState(false)

  useEffect(() => {
    activeRef.current = true
    visibleRef.current = true

    if ("IntersectionObserver" in window && mockRef.current) {
      const observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            visibleRef.current = entry.isIntersecting
            if (entry.isIntersecting) {
              setBarsFilled(true)
            }
          })
        },
        { threshold: 0.25 }
      )
      observer.observe(mockRef.current)

      return () => {
        activeRef.current = false
        observer.disconnect()
      }
    }

    setBarsFilled(true)

    return () => {
      activeRef.current = false
    }
  }, [])

  useEffect(() => {
    const loop = async () => {
      let index = 0

      while (activeRef.current) {
        while (activeRef.current && !visibleRef.current) {
          await wait(250)
        }

        setActiveIndex(index)
        setTotalBump(true)
        await wait(1500)
        setTotalBump(false)
        index = (index + 1) % agencyHeroRetainers.length
        await wait(900)
      }
    }

    loop()

    return () => {
      activeRef.current = false
    }
  }, [])

  const activeRetainer = agencyHeroRetainers[activeIndex]

  return (
    <section className="hero-f">
      <div className="hero-f-inner">
        <div className="hero-f-left">
          <div className="eyebrow">For marketing &amp; SEO agencies</div>
          {/* Handoff v3.7 §03 — Directive H1, verbatim. */}
          <h1>
            Stripe recurring payments and <em>subscription billing software</em> for agencies
          </h1>
          <p className="lede">
            Built for the way you actually bill: monthly <strong>SEO retainers</strong>
            {" "}that auto-renew, <strong>paid-media management fees</strong> that scale
            with spend, and <strong>content subscriptions</strong> on cards instead
            of net-30 invoices.
          </p>
          <div className="cta-row" style={{ display: "flex", gap: 12, marginBottom: 28 }}>
            <a
              href="https://app.moonclerk.com/signup?try=1"
              className="btn btn-accent btn-lg"
              onClick={() =>
                trackCtaClick({
                  label: "Set up a retainer",
                  location: "agencies_hero",
                  destination: "https://app.moonclerk.com/signup?try=1",
                })
              }
            >
              Set up a retainer
            </a>
            <a href="/pricing/" className="btn btn-outline btn-lg">See pricing →</a>
          </div>
          <div className="trust-card">
            <div className="trust-stats">
              <div className="trust-stat"><div className="trust-stat-num">650+</div><div className="trust-stat-label">Agencies billing retainers</div></div>
              <div className="trust-stat"><div className="trust-stat-num">$48K</div><div className="trust-stat-label">Avg monthly retainer book</div></div>
              <div className="trust-stat"><div className="trust-stat-num">12 days</div><div className="trust-stat-label">Faster cash collection</div></div>
            </div>
            <div className="trust-badges">
              <div className="trust-badge"><span><b>Branded</b> checkout links</span></div>
              <div className="trust-badge"><span><b>PCI-DSS</b> Level 1</span></div>
            </div>
          </div>
        </div>
        <div className="hero-f-right">
          <div className="pmock" style={{ maxWidth: 500, marginLeft: "auto" }} ref={mockRef}>
            <div className="pmock-head"><span className="d" /><span className="d" /><span className="d" /><span className="u">app.moonclerk.com/retainers</span></div>
            <div className="pmock-body">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14 }}>
                <div style={{ fontFamily: "'Graphik', system-ui, sans-serif", fontWeight: 500, fontSize: 22, letterSpacing: "-.02em" }}>Retainer book · April<span className="ret-live"><span className="pulse" />Live</span></div>
                <div id="retTotal" className={`ret-total${totalBump ? " bump" : ""}`} style={{ fontFamily: "'Graphik', system-ui, sans-serif", fontWeight: 700, fontSize: 22, color: "var(--teal-dk)" }}><span className="v">$48,200</span><span style={{ fontFamily: "'Graphik', system-ui, sans-serif", fontSize: 11, color: "var(--ink-3)", fontWeight: 400, marginLeft: 4 }}>/mo</span><span className="delta">↑ +${activeRetainer[5]}</span></div>
              </div>
              <div className="ret-grid" id="retGrid">
                {agencyHeroRetainers.map(([client, plan, amount, width, color], index) => (
                  <div className={`ret-card${index === activeIndex && totalBump ? " is-charging" : ""}`} key={client}>
                    <div className="ret-card-head"><div><div className="ret-client">{client}</div><div className="ret-plan">{plan}</div></div><div className="ret-amt">{amount}</div></div>
                    <div className="ret-bar"><div className="ret-bar-fill" style={{ width: barsFilled ? width : "0%", background: color || undefined }} /></div>
                    <div className="ret-bar-labels"><span>Renews May 1</span><span>Active</span></div>
                  </div>
                ))}
              </div>
              <div className="bo-row" style={{ padding: "14px 0 2px", borderBottom: "none" }}>
                <span className="nm" style={{ fontWeight: 600, color: "var(--ink-2)", fontSize: 12 }}>+ 8 more clients</span>
                <span />
                <span style={{ fontSize: 11, color: "var(--teal-dk)", fontWeight: 600 }}>See all →</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

const AgenciesPage = ({ data }) => {
  const page = data.allDatoCmsMcV2PageVertical.nodes[0] || {}
  const sectionCollection =
    page.sectionCollection ||
    pickCollectionNode(data.allDatoCmsMcV2SectionCollection.nodes, null, "agencies-sections")
  const allSections = sectionCollection.contentSections || []
  const heroMetaSection =
    allSections.find(section => section?.sectionKey === "agencies_hero_meta") || null
  const contentSections = allSections.filter(section => section?.sectionKey !== "agencies_hero_meta")
  const proofCollection =
    page.proofCollection ||
    resolveProofCollection(data.allDatoCmsMcV2ProofCollection.nodes, null, "agencies-proof")
  const faqCollection =
    page.faqCollection ||
    resolveFaqCollection(data.allDatoCmsMcV2FaqCollection.nodes, null, "agencies-faq")
  const faqItems = faqCollection.faqItems?.length ? faqCollection.faqItems : agenciesFallbackFaqItems
  const testimonials = proofCollection.testimonials?.length ? proofCollection.testimonials : agenciesFallbackTestimonials
  const managedContentSections = useMemo(() => {
    let sections = contentSections

    sections = ensureSection(sections, {
      sectionKey: "agencies_test_band",
      componentType: "test_band",
      hidden: false,
      headingHtml: "SEO shops, paid-media teams, and full-service growth agencies billing cleanly on MoonClerk",
    })
    sections = ensureSection(sections, {
      sectionKey: "agencies_story_card",
      componentType: "story_card",
      hidden: false,
      eyebrow: "Customer story",
    })
    sections = ensureSection(sections, {
      sectionKey: "agencies_faq",
      componentType: "faq_list",
      hidden: false,
      eyebrow: "FAQ",
      headingHtml: "Questions about Stripe recurring payments <em>for agencies.</em>",
    })
    sections = ensureSection(sections, {
      sectionKey: "agencies_final_cta",
      componentType: "cta",
      hidden: false,
      headingHtml: "Start using recurring invoice software <em>with your agency brand.</em>",
      summary: "Explore free. Invite your account team. Launch branded billing for your first SEO client in an afternoon.",
      primaryCtaLabel: "Explore for free",
      primaryCtaHref: "https://app.moonclerk.com/signup?try=1",
    })
    // Handoff v3.7 §04 — two SEO story sections at Directive's insert points.
    agenciesSeoSections.forEach(seoSection => {
      sections = ensureSection(sections, seoSection)
    })


    return sections
      .map(cleanAgencySection)
      .slice()
      .sort((a, b) => (agenciesSectionOrder[a?.sectionKey] || 999) - (agenciesSectionOrder[b?.sectionKey] || 999))
  }, [contentSections])

  return (
    <CmsPage
      slug={page.slug}
      seo={page.seo}
      eyebrow={page.eyebrow}
      heading="Stripe recurring payments and subscription billing software for agencies"
      summary={String(page.summary || "").replace(/white-label/gi, "branded")}
      body={page.body}
      bodyHeading={page.bodyHeading || "For Agencies"}
      heroMediaType={page.heroMediaType}
      heroImageUrl={page.heroImageUrl}
      heroImageAlt={page.heroImageAlt}
      heroVideoUrl={page.heroVideoUrl}
      heroVideoPosterUrl={page.heroVideoPosterUrl}
      heroSlot={<AgenciesHero />}
      heroMetaSection={heroMetaSection}
      contentSections={managedContentSections}
      testimonials={testimonials}
      faqItems={faqItems}
      contentHtml={page.contentHtml}
      fallbackContentHtml={prototypeSlice.contentHtml}
      extraStyles={agenciesExtraStyles}
      extraHeadLinks={prototypeSlice.extraHeadLinks}
      emptyMessage="Create or seed the agencies record in mc_v2_page_vertical."
    />
  )
}

export default AgenciesPage

export const query = graphql`
  query AgenciesCmsPageQuery {
    allDatoCmsMcV2PageVertical(filter: { slug: { eq: "agencies" } }, limit: 1) {
      nodes {
        seo {
          ...CmsSeoFields
        }
        slug
        eyebrow
        heading
        summary
        body
        # bodyHeading  # TODO(T-014 add the Dato field, T-015 then uncomment): blocked on DatoCMS schema
        heroMediaType
        heroImageUrl
        heroImageAlt
        heroVideoUrl
        heroVideoPosterUrl
        contentHtml
        sectionCollection {
          contentSections {
            ...CmsHtmlSectionFields
          }
        }
        proofCollection {
          sourceCollection {
            key
          }
          selectedProfileEntries {
            quote
            name
            initials
            role
            industry
            stat
            logoSvg
            gradient
          }
          selectedTestimonialEntries {
            quote
            customerName
            customerInitials
            customerRole
            industry
            brandName
            metaOne
            metaTwo
            metaThree
            statOneValue
            statOneLabel
            statTwoValue
            statTwoLabel
            statThreeValue
            statThreeLabel
          }
          testimonialEntries {
            quote
            customerName
            customerInitials
            customerRole
            industry
            brandName
            metaOne
            metaTwo
            metaThree
            statOneValue
            statOneLabel
            statTwoValue
            statTwoLabel
            statThreeValue
            statThreeLabel
          }
        }
        faqCollection {
          sourceCollection {
            key
          }
          selectedFaqEntries {
            question
            answer
          }
        }
      }
    }
    allDatoCmsMcV2SectionCollection {
      nodes {
        key
        contentSections {
          ...CmsHtmlSectionFields
        }
      }
    }
    allDatoCmsMcV2ProofCollection {
      nodes {
        key
        sourceCollection {
          key
        }
        selectedProfileEntries {
          quote
          name
          initials
          role
          industry
          stat
          logoSvg
          gradient
        }
        selectedTestimonialEntries {
          quote
          customerName
          customerInitials
          customerRole
          industry
          brandName
          metaOne
          metaTwo
          metaThree
          statOneValue
          statOneLabel
          statTwoValue
          statTwoLabel
          statThreeValue
          statThreeLabel
        }
        testimonialEntries {
          quote
          customerName
          customerInitials
          customerRole
          industry
          brandName
          metaOne
          metaTwo
          metaThree
          statOneValue
          statOneLabel
          statTwoValue
          statTwoLabel
          statThreeValue
          statThreeLabel
        }
      }
    }
    allDatoCmsMcV2FaqCollection {
      nodes {
        key
        sourceCollection {
          key
        }
        selectedFaqEntries {
          question
          answer
        }
      }
    }
  }
`
